"use client";

import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { clsx } from "clsx";

import styles from "./ReportTable.module.css";
import type { TableVizSpec } from "@/lib/reports/schema";
import { formatValue, humanizeAny } from "@/lib/reports/labels";

export interface ReportTableRow extends Record<string, number | string | null> {}

export interface ReportTableProps {
  viz: TableVizSpec;
  rows: ReportTableRow[];
  rowHeight?: number;
  emptyState?: React.ReactNode;
  /** Density inherited from the Block; overrides viz.density. */
  density?: "compact" | "comfortable" | "spacious";
}

/**
 * Derive table columns from spec + rows. Pure; exported for tests.
 *
 * When `viz.columns` is non-empty, it wins (user/AI authored the table
 * intentionally). When it's empty (a common AI failure mode caught in
 * Q63), we fall back to introspecting the first row's keys + types.
 */
export function deriveTableColumns(
  vizColumns: TableVizSpec["columns"],
  rows: ReportTableRow[],
): TableVizSpec["columns"] {
  if (vizColumns.length > 0) return vizColumns;
  const probe = rows[0] ?? {};
  return Object.keys(probe).map((key) => {
    const sample = probe[key];
    const isNumeric = typeof sample === "number";
    return {
      key,
      label: humanizeAny(key),
      format: isNumeric ? ("number" as const) : undefined,
      sortable: true,
    };
  });
}

/**
 * Format a single table cell value given its column metadata.
 * Pure; exported for tests.
 *
 * Order of precedence:
 *   1. Explicit col.format → formatValue with that format
 *   2. Numeric runtime value → formatValue with "number"
 *   3. Empty / null / undefined → "—"
 *   4. String → strip leaking "Cube.field-" prefix
 */
export function formatTableCell(
  value: unknown,
  format: import("@/lib/reports/schema").NumberFormat | undefined,
): string {
  if (format) return formatValue(value, format);
  if (typeof value === "number") return formatValue(value, "number");
  if (value == null || value === "") return "—";
  return String(value).replace(/^[A-Z][A-Za-z]+\.[a-zA-Z]+[-_]/, "");
}

export function ReportTable({ viz, rows, rowHeight = 36, emptyState, density }: ReportTableProps) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const effectiveDensity = density ?? viz.density;

  // Auto-derive columns from the first row when the AI/spec emitted
  // `columns: []`. Without this fallback the table renders a blank canvas
  // (caught in eval-bar-recruiter-leaderboard-emails — AI correctly
  // picked block.type=table but left columns empty).
  const effectiveColumns = React.useMemo(
    () => deriveTableColumns(viz.columns, rows),
    [viz.columns, rows],
  );

  const columns = React.useMemo<ColumnDef<ReportTableRow>[]>(
    () =>
      effectiveColumns.map((col) => ({
        id: col.key,
        accessorKey: col.key,
        // Always humanise the header — even when the user (or AI) gave a
        // label, we still strip Cube-namespaces / snake_case for display.
        header: displayTableHeader(col),
        enableSorting: col.sortable !== false,
        cell: ({ getValue }) => formatTableCell(getValue(), col.format),
        meta: {
          align: col.align ?? (col.format && col.format !== "compact" ? "right" : "left"),
          // Per-format minimum column width. Currency + duration_minutes
          // need ~110px ("$12,345" / "1h 23m"); plain numbers ~90px
          // ("1,234,567"); percent ~70px ("99.5%"); compact ~70px
          // ("12.4k"). User-set width wins.
          minWidth:
            col.width ??
            (col.format === "currency"
              ? 110
              : col.format === "duration_minutes" || col.format === "duration_seconds"
                ? 100
                : col.format === "percent" || col.format === "compact"
                  ? 72
                  : col.format === "number"
                    ? 92
                    : undefined),
        },
      })),
    [effectiveColumns],
  );

  const [sorting, setSorting] = React.useState<SortingState>([]);

  // TanStack Table's useReactTable() returns functions the React Compiler
  // cannot memoize, so it already skips this component — the warning is
  // expected and harmless here.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableRows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  if (rows.length === 0) {
    return <div className={styles.empty}>{emptyState ?? "No data to show."}</div>;
  }

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  return (
    <div ref={scrollerRef} className={clsx(styles.scroller, styles[`density-${effectiveDensity}`])}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const sortable = header.column.getCanSort();
                const sort = header.column.getIsSorted();
                const meta = header.column.columnDef.meta as { align?: "left" | "center" | "right"; minWidth?: number } | undefined;
                const align = meta?.align ?? "left";
                const minWidth = meta?.minWidth;
                return (
                  <th
                    key={header.id}
                    className={styles.th}
                    data-sortable={sortable || undefined}
                    data-aligned={align}
                    style={minWidth ? { minWidth } : undefined}
                    onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {sort ? (
                      <span className={styles.sortIndicator}>{sort === "asc" ? "▲" : "▼"}</span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 ? (
            <tr><td colSpan={columns.length} style={{ height: paddingTop }} /></tr>
          ) : null}
          {virtualItems.map((vItem) => {
            const row = tableRows[vItem.index];
            return (
              <tr key={row.id} className={styles.row} style={{ height: rowHeight }}>
                {row.getVisibleCells().map((cell) => {
                  const align = (cell.column.columnDef.meta as { align?: "left" | "center" | "right" } | undefined)?.align ?? "left";
                  return (
                    <td key={cell.id} className={styles.td} data-aligned={align}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {paddingBottom > 0 ? (
            <tr><td colSpan={columns.length} style={{ height: paddingBottom }} /></tr>
          ) : null}
        </tbody>
        {viz.showColumnTotals && rows.length > 1 ? (
          <TableFooterTotals columns={effectiveColumns} rows={rows} />
        ) : null}
      </table>
    </div>
  );
}

function displayTableHeader(col: TableVizSpec["columns"][number]): string {
  const label = col.label?.trim();
  const keyLabel = humanizeAny(col.key);
  if (!label || /^spec[\s._-]/i.test(label)) return keyLabel;
  const displayLabel = humanizeAny(label);
  if (/\bid$/i.test(displayLabel) && keyLabel !== displayLabel) return keyLabel;
  return displayLabel;
}

/**
 * Render a single totals row at the bottom of the table. Sums any
 * numeric column whose format is "number" / "compact" / "currency".
 * Skips dimensions (first column conventionally) and percent / rate
 * columns where a sum is meaningless. The first column shows "Total".
 *
 * Receives the same `effectiveColumns` the body renders (auto-derived when
 * `viz.columns` is empty) so the footer cells line up with the body instead
 * of collapsing to an empty row.
 */
function TableFooterTotals({
  columns,
  rows,
}: {
  columns: TableVizSpec["columns"];
  rows: ReportTableRow[];
}) {
  const totals = columns.map((col, idx) => {
    if (idx === 0) return null;                        // first column = label
    if (col.format === "percent" || col.format === "duration_minutes" || col.format === "duration_seconds") return null;
    let sum = 0;
    let anyNumeric = false;
    for (const r of rows) {
      const v = r[col.key];
      if (typeof v === "number" && Number.isFinite(v)) {
        sum += v;
        anyNumeric = true;
      }
    }
    return anyNumeric ? sum : null;
  });
  return (
    <tfoot className={styles.tfoot}>
      <tr style={{ borderTop: "2px solid var(--border-default, rgba(17,24,39,0.16))" }}>
        {columns.map((col, idx) => {
          const align = col.align ?? (col.format && col.format !== "compact" ? "right" : "left");
          if (idx === 0) {
            return (
              <td key={col.key} className={styles.td} data-aligned={align} style={{ fontWeight: 600 }}>
                Total
              </td>
            );
          }
          const v = totals[idx];
          return (
            <td key={col.key} className={styles.td} data-aligned={align} style={{ fontWeight: 600 }}>
              {v === null ? "" : formatValue(v, col.format)}
            </td>
          );
        })}
      </tr>
    </tfoot>
  );
}
